import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import BadgePopup from "../components/Badgepopup";

import { Link } from "react-router-dom";

function Lightning91() {
   const [isOpen, setIsOpen] = useState(true);
  const contentRef = useRef(null);
  const titleText = "A Primer on What The Lightning Network is";
  const links = {
    prev: "/index-lightning",
    index: "/index-lightning",
    next: "/lightning-92",
  };
  const questions = [
    {
      text: "1. What is the main purpose of the Lightning Network in relation to Bitcoin?",
      options: [
        {
          value: "A",
          label: "A. To replace Bitcoin’s base layer entirely",
        },
        {
          value: "B",
          label: "B. To act as a second-layer payment system on top of Bitcoin",
        },
        {
          value: "C",
          label: "C. To increase Bitcoin’s block size",
        },
        {
          value: "D",
          label: "D. To centralize Bitcoin transactions",
        },
      ],
      correct: "B",
    },
    {
      text: "2. Why would putting all micro-transactions on Bitcoin’s base layer create problems?",
      options: [
        {
          value: "A",
          label: "A. The base layer does not support digital signatures",
        },
        {
          value: "B",
          label: "B. The base layer has infinite block space",
        },
        {
          value: "C",
          label:
            "C. It would fill up limited block space and make fees extremely high",
        },
        {
          value: "D",
          label: "D. It would cause Bitcoin to lose decentralization",
        },
      ],
      correct: "C",
    },
    {
      text: "3. How does the Lightning Network help Bitcoin scale globally??",
      options: [
        {
          value: "A",
          label: "A. By reducing Bitcoin’s supply",
        },
        {
          value: "B",
          label: "B. By allowing unlimited on-chain transactions",
        },
        {
          value: "C",
          label:
            "C. By enabling users to transact off-chain and only settle the final results on-chain",
        },
        {
          value: "D",
          label: "D. By removing miners from the network",
        },
      ],
      correct: "C",
    },
    {
      text: "4. What feature makes the Lightning Network trust-less?",
      options: [
        { value: "A", label: "A. It relies on banks to approve transactions" },
        {
          value: "B",
          label: "B. It uses a central server to validate payments",
        },
        { value: "C", label: "C. It removes incentives from the system" },
        {
          value: "D",
          label:
            "D. It uses game-theoretic incentives and does not require trusting a central authority",
        },
      ],
      correct: "D",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
        <LandingSection
          titleText={titleText}
          scrollToRef={contentRef}
          progressValue={91}
          timerMinutes={90}
          currentLevel={7}
        />
      <p className="content" ref={contentRef}>
      
        Welcome to <b>Day 91</b> of your learning journey, where we begin
        exploring one of the most important innovations built on top of Bitcoin:
        the Lightning Network. In this lesson, you will discover how Lightning
        transforms Bitcoin from a powerful settlement system into a fast and
        scalable payment network capable of serving billions. We will walk
        through the core ideas behind this second-layer protocol, why it is
        essential for Bitcoin’s growth, and how it solves the challenges of
        speed, cost and scalability without compromising Bitcoin’s trust-less
        foundations.
        <br />
        <br />
        <b>Bitcoin's Global Payment Layer</b>
        <br />
        <br />
        Bitcoin, in its original form, is designed to serve as the most secure
        and decentralized monetary system ever created. Its base layer, commonly
        referred to as Layer 1, is engineered with absolute priority given to
        security, immutability, and resistance to censorship. These properties
        make the Bitcoin blockchain incredibly robust but also limit the number
        of transactions it can process every second. As global adoption grows, a
        natural and obvious question arises: how can billions of people use
        Bitcoin for everyday payments without overloading the blockchain? The
        answer lies in Bitcoin’s Layer 2 solutions, the most prominent of which
        is the Lightning Network.
        <br />
        The Lightning Network is a second-layer protocol built on top of
        Bitcoin. It is not a separate coin, a different blockchain, or an
        alternative system. Instead, it is an extension of Bitcoin, using its
        strong security guarantees while enabling faster and cheaper
        transactions. This division of responsibilities between layers is
        intentional: Bitcoin’s base layer continues to function as the final
        settlement layer, while the Lightning Network acts as the payment layer
        where high-speed, low-cost transfers occur.
        <br />
        To understand Lightning, it is helpful to compare the structure to
        traditional finance. In the conventional banking world, large
        settlements between banks occur through slow, high-security systems like
        SWIFT or ACH. Meanwhile, customers get a much faster payment experiences
        through layers built on top of those systems, such as debit cards,
        online transfers, and digital wallets. These upper layers provide speed
        and convenience, while the settlement layer ensures finality and
        security. Bitcoin’s ecosystem is evolving in a similar layered
        direction, except with one major difference: the Lightning Network
        operates without central banks, private institutions, or trusted
        intermediaries. It is entirely decentralized, powered by mathematics,
        cryptography, and game-theoretic incentives.
        <br />
        The vision for the Lightning Network was first outlined in a 2015
        whitepaper by Joseph Poon and Thaddeus Dryja. Their proposal introduced
        a way for Bitcoin users to transact with each other instantly while only
        occasionally interacting with the blockchain. The first functioning
        Lightning implementations were released in 2018, and since then, the
        network has grown into a vital part of Bitcoin’s global payment
        infrastructure. Today, Lightning is being used by individuals,
        businesses, and even entire countries to enable instant and reliable
        Bitcoin payments.
        <br />
        At its core, the Lightning Network is a trust-less system. This means
        that you do not need to rely on any central authority, company, or bank
        to send or receive payments. The system is designed so that participants
        are protected by cryptographic guarantees and economic incentives rather
        than trust. Lightning’s design relies heavily on game theory, a field of
        mathematics that studies incentives, strategic interactions, and
        decision-making. The idea is simple: the rules of the protocol make it
        in the best interest of every participant to behave honestly. If someone
        tries to cheat, the system penalizes them automatically, and they lose
        money. If they participate correctly, they are rewarded or at least
        protected. This alignment of incentives ensures that the network remains
        secure without needing centralized oversight.
        <br />
        Before diving deeper into technical concepts like payment channels,
        nodes, routing, and settlement, it is essential to build a strong
        conceptual foundation. The Lightning Network can sound intimidating at
        first, but the underlying idea is surprisingly simple when approached
        with everyday analogies. To illustrate this, consider the idea of
        opening a payment “tab” with a friend.
        <br />
        Imagine you and your friend frequently buy each other tea. Each time you
        pay for their tea or they pay for yours, you don’t immediately exchange
        cash. Instead, you jot down the amount on a shared piece of paper. You
        might add to the tab ten or twenty times over a week. Only when you
        decide to close the tab do you settle the final balance in cash. Instead
        of exchanging money for every small purchase, you keep a running log and
        pay the net amount at the end.
        <br />
        This is essentially how Lightning payment channels work. When two
        Bitcoin users open a channel, they perform a single Bitcoin transaction
        on the main blockchain that locks some funds into a shared contract.
        After this channel is open, they can send unlimited payments back and
        forth between each other instantly, privately, and without touching the
        blockchain again. These fast transactions occur “off-chain,” similar to
        updating the shared tab. When either participant wants to close the
        channel, the final balance i.e the net result of all their off-chain
        payments is settled back onto the Bitcoin blockchain.
        <br />
        Because only the opening and closing of a Lightning channel require
        blockchain confirmations, the number of transactions that can happen on
        Lightning is limited only by network connectivity and liquidity, not by
        block space. This allows Lightning to scale Bitcoin to millions of
        payments per second without placing strain on the blockchain.
        <br />
      </p>
      <img src="ln.png"></img>
      <table className="trade-table">
        <thead>
          <tr>
            <th>Possible Payment Routes</th>
          </tr>
          <tr>
            <td> ✅ Amina → Bilal </td>
          </tr>
          <tr>
            <td> ✅ Bilal → Amina </td>
          </tr>
          <tr>
            <td> ✅ Bilal → Careem </td>
          </tr>
          <tr>
            <td> ✅ Careem → Bilal </td>
          </tr>
          <tr>
            <td> ❌ Amina → Careem </td>
          </tr>
          <tr>
            <td> ❌ Careem → Amina </td>
          </tr>
          <tr>
            <td> ✅ Amina → Bilal → Careem </td>
          </tr>
          <tr>
            <td> ✅ Careem → Amina → Bilal </td>
          </tr>
        </thead>
      </table>
      <p className="content">
        But the Lightning Network is more powerful than just two people settling
        a tab. The real innovation comes from the fact that channels are part of
        a connected network. If Amina has a channel with Bilal, and Bilal has a
        channel with Careem, then Amina can pay Careem even if she does not have
        a direct channel with him. The payment finds a route through Bilal, who
        forwards it without ever taking custody of the funds. This routing
        mechanism is similar to how data finds its path across the internet.
        When you send an email, your message doesn’t go directly from your
        computer to the receiver’s device. Instead, it passes through a series
        of routers that direct it toward the final destination. The routers do
        not need to know you or trust you. They simply follow the protocol’s
        rules to forward your message. The Lightning Network works in the same
        distributed and trust-less manner.
        <br />
        Routing is where game theory becomes critical. Since the network depends
        on forwarding nodes for payments to move, these nodes need to be
        incentivized to behave honestly. They earn small routing fees whenever
        they pass along payments, giving them an economic reason to participate
        and maintain liquidity. If they misbehave or attempt to cheat, the
        protocol penalizes them through cryptographic mechanisms that protect
        users’ funds. This creates a self-sustaining ecosystem where honest
        participation is the most profitable strategy.
        <br />
        Another important concept is the role of Bitcoin’s base layer as the
        final settlement mechanism. Lightning channels are governed by Bitcoin
        smart contracts that ensure every participant can reclaim their funds
        safely, even if other parties go offline or act maliciously. These
        contracts are enforced by Bitcoin’s blockchain, which serves as the
        ultimate judge. Imagine the main Bitcoin chain as a supreme court.
        Lightning handles everyday disputes privately and instantly, but if
        something goes wrong, any participant can escalate the issue to the base
        layer for guaranteed enforcement. This relationship between the layers
        ensures both speed and security.
        <br />
        Now, let us expand the analogy further. Returning to the idea of a tab
        between friends, imagine that instead of keeping the tab on a simple
        piece of paper, you have a digital ledger that updates automatically and
        securely whenever one of you buys something. Neither of you can cheat or
        change the numbers unfairly because the ledger uses cryptographic rules
        that protect the integrity of the information. In Lightning, this ledger
        is called a “commitment transaction.” Every time the balance between the
        two channel participants changes, a new commitment transaction is
        created. This transaction reflects the updated distribution of funds and
        is signed by both parties. If either party ever tries to cheat by
        broadcasting an old version of the ledger that favors them, the other
        party can prove the cheating attempt and claim all the funds in the
        channel. This is where the game-theoretic penalties come into play.
        <br />
        The beauty of the Lightning Network is that it transforms Bitcoin from a
        system capable of handling a few transactions per second into a network
        capable of supporting millions or even billions of payments, all without
        sacrificing decentralization. Traditional payment systems rely heavily
        on centralized intermediaries who have the power to censor transactions,
        freeze accounts, impose fees, or block users based on geography,
        politics, or corporate policy. Lightning, being decentralized and
        peer-to-peer, cannot discriminate in the same way. Anyone with a
        smartphone can participate. There are no account forms, no
        identification requirements, and no middlemen dictating who can
        transact.
        <br />
        This makes Lightning especially powerful in regions where access to
        traditional banking is limited or where financial censorship is common.
        For people living under unstable governments, restrictive monetary
        policies, or high inflation, Lightning offers a path to financial
        inclusion using a system that cannot be shut down or manipulated.
        Because Lightning operates on open standards, developers around the
        world can build wallets, services, and tools that integrate seamlessly
        into the network. The ecosystem is constantly growing, with new
        innovations appearing every year to improve usability, privacy,
        liquidity management, and routing reliability.
        <br />
        Beyond person-to-person payments, Lightning also enables entirely new
        use cases that were previously impractical on Bitcoin’s base layer.
        Micro-payments are a prime example. Sending fractions of a cent using
        traditional payment methods is impossible due to processing fees. With
        Lightning, payments can be very small, enabling new business models such
        as pay-per-article, tipping, machine-to-machine payments, streaming
        money, and dynamic pricing. These kinds of interactions open the door to
        a new digital economy where value can move as freely as information.
        <br />
        It is important to understand why Lightning remains fundamentally
        optional. Not every Bitcoin user must interact with Lightning, nor does
        Lightning replace the base layer. Instead, both layers complement each
        other. Users who prefer to store their wealth long-term can keep their
        coins on-chain. Those who need fast everyday transactions can use
        Lightning. This modular structure allows Bitcoin to scale while staying
        true to its principles of decentralization and sound monetary design.
        <br />
        <br />
        <b>Scaling, Fees, and Everyday Payments</b>
        <br />
        <br />
        Bitcoin’s base layer is one of the most extraordinary technological
        achievements of the modern era. It functions as a global, decentralized,
        and tamper-resistant ledger that records every transaction forever. This
        permanent and immutable recording is what gives Bitcoin its strength,
        reliability, and trustlessness. When people say Bitcoin is transparent
        and secure, they are referring to the property that every transaction is
        written to a blockchain that anyone can verify independently. But this
        same feature of permanence, which is essential for sound money, also
        creates natural limitations in terms of scalability. Since every
        transaction must be recorded on the blockchain and every full node
        around the world must store and validate this data, the system must be
        designed to grow at a pace that is manageable for ordinary individuals.
        If the blockchain becomes too large or grows too quickly, running a node
        would become expensive and difficult, eventually pushing out smaller
        participants and concentrating the network among a select few. This
        would undermine the very decentralization that makes Bitcoin valuable in
        the first place.
        <br />
        Because Bitcoin’s design values decentralization above everything else,
        its block size is intentionally limited and its block time is
        intentionally set to roughly ten minutes. These decisions force the
        blockchain to grow at a predictable and moderate pace, ensuring that
        ordinary people can continue to participate in running nodes without
        needing specialized hardware or government-level resources. However,
        these same design decisions introduce constraints. A limited block size
        means that only a limited number of transactions can be included in each
        block. A ten-minute interval between blocks means that transactions must
        sometimes wait for confirmation, especially when the network is busy.
        These characteristics make Bitcoin’s base layer robust, secure, and
        highly decentralized, but they also make it unfit for handling millions
        of daily micro-transactions directly on-chain. If the entire world tried
        to process every small payment on Bitcoin’s base layer, it would lead to
        serious consequences. The mempool would remain permanently congested,
        fee levels would become prohibitive, and the blockchain would expand too
        quickly for ordinary people to keep up.
        <br />
        This is where the Lightning Network becomes essential. The Lightning
        Network is a layer built on top of Bitcoin that allows users to transact
        at high speed and low cost without placing every payment on the
        blockchain. Understanding why Lightning is necessary requires
        understanding that Bitcoin’s base layer is fundamentally a settlement
        layer, similar to how global banking systems operate behind the scenes.
        In traditional finance, settling funds between banks is a slow and
        deliberate process that is not visible to the customer. When you pay
        with a card, the payment looks instant, but the actual settlement
        between institutions happens much later. Bitcoin’s base layer functions
        similarly; it excels at secure and final settlement of value, not at
        handling everyday payments requiring instant speed. The Lightning
        Network overlays this base layer, turning Bitcoin into a fast and
        efficient payment system while still preserving the decentralization and
        immutability of the underlying blockchain.
        <br />
        A crucial limitation of Bitcoin’s base layer is that block space is
        scarce. Each block can only fit a limited number of transactions, and
        because blocks are mined at set intervals, there is a finite supply of
        transaction space being produced every day. Demand for this space
        varies, and when demand exceeds supply, fees rise. This system is
        economically elegant because it incentivizes people to use the
        blockchain efficiently rather than spamming it unnecessarily. But it
        also means that if large numbers of people attempt to conduct many small
        transactions directly on the blockchain, fees will skyrocket. High fees
        are acceptable for large settlement transactions, moving a significant
        sum of money, opening a cold storage vault, executing a multisig update,
        or performing business-level financial operations. They are not
        acceptable for buying coffee, tipping a creator, paying for a subway
        ride, or sending a few cents across the world. This mismatch highlights
        the need for a second layer like Lightning. Lightning allows users to
        conduct countless transactions instantly and cheaply without touching
        the blockchain each time.
        <br />
        Lightning also solves a practical issue with Bitcoin,the ten-minute
        block time. While the slow block time is a deliberate design choice that
        strengthens Bitcoin’s security, it is inconvenient for everyday
        purchases. Most people do not want to wait even a few minutes for
        confirmation when paying for something small. Merchants also do not want
        to risk accepting zero-confirmation transactions, as such payments can
        be reversed by the sender through replace-by-fee. Lightning eliminates
        this problem entirely by enabling instant, irreversible payments that
        settle within milliseconds. This makes Bitcoin usable for paying at
        restaurants, shops, public transport systems, vending machines, or
        anywhere else you would normally expect rapid settlement. In real life,
        waiting for confirmation simply does not fit into consumer behavior
        patterns. Lightning bridges this gap by giving Bitcoin the speed that
        users expect while still relying on the security of the base layer.
        <br />
        If Bitcoin attempted to process every micro-transaction directly
        on-chain, the consequences would be severe. The mempool would be
        constantly full, with transactions waiting for long periods before being
        confirmed. Fees would rise to levels that make daily usage impossible
        for regular people. The blockchain would grow too rapidly, requiring
        increasing amounts of storage and bandwidth to run a full node. Over
        time, this would lead to centralization, as fewer individuals would be
        capable of maintaining a node. This scenario contradicts Bitcoin’s
        mission because decentralization is not merely an ideal; it is the
        mechanism that keeps Bitcoin secure and censorship-resistant. Lightning
        offers a way to scale Bitcoin without pushing it toward centralization.
        <br />
        Another important aspect of Lightning is its ability to facilitate
        micro-transactions that are not feasible on the base layer. Because
        on-chain transactions incur fees, sending extremely small amounts
        becomes impractical. Lightning changes this by enabling payments
        measured in tiny fractions. This unlocks new economic models that were
        impossible in the past, such as pay-per-use systems, real-time streaming
        payments, tipping mechanisms, machine-to-machine interactions, and
        micropayments for online content. Instead of subscribing monthly to a
        service, a user could pay by the second for what they consume. Instead
        of content creators relying on ad revenue, they could be supported
        directly by viewers sending micro-payments. Instead of IoT devices being
        restricted by banking limitations, they could autonomously transact with
        each other in tiny increments. Lightning introduces these possibilities
        while keeping Bitcoin’s core layer secure and uncluttered.
        <br />
        One of the most important reasons Lightning is necessary is that
        Bitcoin’s block space is intentionally finite. Unlike many alternative
        cryptocurrencies that increase their block sizes to boost throughput at
        the cost of centralization, Bitcoin prioritizes long-term
        sustainability. The decision to restrict block size is rooted in the
        belief that the ability for anyone to run a node is essential to
        Bitcoin’s mission. Increasing block size might temporarily increase
        transaction capacity, but it would eventually lead to higher hardware
        requirements, reduced node participation, and greater centralization.
        Lightning provides a far better solution because it preserves the
        integrity of the base layer while enabling scaling in an efficient and
        decentralized manner. Instead of competing for block space, Lightning
        users transact freely off-chain and use the blockchain only for
        settlement, as it was originally intended.
        <br />
        Lightning therefore acts as a critical pressure valve. During periods of
        high demand, the base layer can become congested, but Lightning allows
        users to bypass this congestion by moving their activity off-chain. This
        keeps the base layer functioning smoothly even during periods of extreme
        market excitement or network usage. By maintaining a steady load on the
        blockchain, Lightning ensures that long-term settlement transactions can
        continue to be executed reliably. This balanced relationship between the
        base layer and Lightning creates a harmonious system where each layer
        fulfills a specific role. The base layer provides security and final
        settlement, while Lightning offers speed, efficiency, and affordability.
        <br />
        For Bitcoin to function as global money, it must work at all levels of
        economic activity. It must be capable of handling large transactions
        that require strong security, but it must also support small, everyday
        payments. A currency that is only good for savings and large transfers
        can never serve as true money. True money must circulate freely, and for
        that circulation to happen at scale, it must be cheap and fast.
        Lightning is what enables Bitcoin to function like cash in the digital
        world. With Lightning, Bitcoin is no longer only a store of value; it
        becomes a medium of exchange suitable for daily use.
        <br />
        Lightning payments settle instantly and irreversibly. There are no
        chargebacks, no card processors, no banks, and no hidden fees. The
        merchant receives funds immediately and securely, anywhere in the world.
        This stands in stark contrast to traditional payment systems that often
        involve multiple intermediaries, slow settlement times, and high fees.
        Even modern digital payment apps rely on centralized infrastructure and
        do not allow true global interoperability. Lightning payments, by
        comparison, work across borders without friction. Someone in one part of
        the world can pay someone on the other side within seconds, at almost
        zero cost, and with no intermediary permission. This kind of freedom is
        unprecedented in the history of money.
        <br />
        Another advantage of Lightning is that its fees remain extraordinarily
        low. Base layer fees fluctuate with demand, but Lightning fees typically
        remain tiny because they reflect routing costs within a decentralized
        network of nodes rather than competing for scarce block space. This
        creates a competitive and efficient fee environment where users are not
        penalized for making small payments. The low cost of Lightning
        transactions is what makes micro-payments economically viable, and it is
        part of what makes the Lightning Network a powerful tool for the future
        of online payments.
        <br />
        Overall, Lightning combines speed, efficiency, and decentralization in a
        way that preserves Bitcoin’s core principles while dramatically
        expanding its usefulness. It allows Bitcoin to scale to millions or even
        billions of transactions per second without compromising the base layer.
        It maintains the base layer’s stability by reducing unnecessary on-chain
        activity. It ensures that Bitcoin can function as a real-world payment
        method while remaining sound money. Without Lightning, Bitcoin would
        face limitations that could slow its adoption and hinder its
        practicality in everyday life. With Lightning, Bitcoin becomes a
        complete monetary system capable of supporting global economic activity
        while remaining decentralized, secure, and efficient.
        <br />
        Lightning is therefore not simply a convenience or an optional addition
        to Bitcoin. It is an essential component of Bitcoin’s evolution. It
        enables a layered architecture where the base layer provides
        foundational security and Lightning provides the speed and scalability
        required for widespread use. This layered design mirrors the structure
        of the internet, where foundational protocols ensure reliability while
        higher layers enable usability. Just as the internet could not scale
        through a single layer alone, Bitcoin cannot scale through its base
        layer alone. Lightning fills this role with elegance and mathematical
        precision. It transforms Bitcoin from a settlement network into a
        full-fledged monetary network capable of handling every level of
        transaction imaginable.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today's lesson introduce learners to the purpose, structure and
        necessity of the Bitcoin Lightning Network. The first section explains
        that Lightning is a second layer built on top of Bitcoin, designed to
        function as its payment layer while the Bitcoin blockchain continues to
        operate as the settlement layer. It highlights how Lightning uses game
        theoretic incentives to remain trust-less, allowing users to transact
        without depending on central authorities. The second section shows why
        Lightning is essential for Bitcoin’s long-term scalability. Since every
        base-layer transaction must be recorded on the immutable blockchain,
        storing all micro-transactions on-chain would overwhelm nodes and make
        Bitcoin too expensive and slow for everyday use. Lightning solves this
        by allowing users to transact off-chain and settle only the final state
        on the blockchain, enabling instant payments, reduced fees and
        preservation of Bitcoin’s precious block space. Today’s lesson
        establishes why Lightning is not just an add-on but a critical part of
        Bitcoin’s path toward becoming global money.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
       <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 7"
        badgeImg="./achieve.png"
        heading="Lightning Pathfinder"
        subtitle="Beginner – Fundamentals of the Lightning Network"
        mainImg="./lightningpathfinder.png"
        description="Awarded for taking your first steps beyond Bitcoin’s base layer. You have begun understanding payment channels, off-chain transactions, and why Layer Two solutions are essential for scalability without compromising sound money principles."
      />
    </div>
  );
}

export default Lightning91;
